/**
 * Camera module for AppBouncer SDK
 * Handles camera access, video stream management, and canvas overlay synchronization
 */

import { t } from '../i18n';

export interface CameraConstraints {
  facingMode?: 'user' | 'environment';
  width?: { min?: number; ideal?: number; max?: number };
  height?: { min?: number; ideal?: number; max?: number };
}

export interface CameraConfig {
  /** Custom video constraints (optional - defaults to adaptive mobile/desktop) */
  constraints?: CameraConstraints;
  /** Mirror the video horizontally for selfie mode (default: true) */
  mirror?: boolean;
  /** Callback when camera state changes */
  onStateChange?: (state: CameraState) => void;
  /** Callback when an error occurs */
  onError?: (error: CameraErrorInfo) => void;
}

export type CameraState = 'idle' | 'requesting' | 'streaming' | 'paused' | 'stopped' | 'error';

/**
 * Camera error information (renamed from CameraError to avoid conflict with CameraError class in errors/)
 */
export interface CameraErrorInfo {
  code: CameraErrorCode;
  message: string;
  originalError?: Error;
}

export type CameraErrorCode =
  | 'PERMISSION_DENIED'
  | 'NOT_FOUND'
  | 'NOT_READABLE'
  | 'OVERCONSTRAINED'
  | 'STREAM_ERROR'
  | 'UNKNOWN';

/**
 * Detects if the current device is mobile
 */
export function isMobileDevice(): boolean {
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * Get default camera constraints based on device type
 */
export function getDefaultConstraints(): MediaStreamConstraints {
  const isMobile = isMobileDevice();

  return {
    video: {
      facingMode: { ideal: 'user' },
      width: isMobile ? { ideal: 480 } : { min: 640, ideal: 1280, max: 1920 },
      height: isMobile ? { ideal: 640 } : { min: 480, ideal: 720, max: 1080 },
    },
    audio: false,
  };
}

/**
 * Maps MediaDevices errors to CameraErrorCode
 */
function mapErrorCode(error: Error): CameraErrorCode {
  if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
    return 'PERMISSION_DENIED';
  }
  if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
    return 'NOT_FOUND';
  }
  if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
    return 'NOT_READABLE';
  }
  if (error.name === 'OverconstrainedError' || error.name === 'ConstraintNotSatisfiedError') {
    return 'OVERCONSTRAINED';
  }
  return 'UNKNOWN';
}

/**
 * Camera manager class for handling video streams
 */
export class CameraManager {
  private stream: MediaStream | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private overlayCanvas: HTMLCanvasElement | null = null;
  private state: CameraState = 'idle';
  private config: CameraConfig;
  private resizeObserver: ResizeObserver | null = null;
  private boundResizeHandler: (() => void) | null = null;

  constructor(config: CameraConfig = {}) {
    this.config = {
      mirror: true,
      ...config,
    };
  }

  /**
   * Get current camera state
   */
  getState(): CameraState {
    return this.state;
  }

  /**
   * Get the active media stream
   */
  getStream(): MediaStream | null {
    return this.stream;
  }

  /**
   * Get video element dimensions
   */
  getVideoDimensions(): { width: number; height: number } | null {
    if (!this.videoElement) return null;
    return {
      width: this.videoElement.videoWidth,
      height: this.videoElement.videoHeight,
    };
  }

  /**
   * Set camera state and notify listeners
   */
  private setState(newState: CameraState): void {
    this.state = newState;
    this.config.onStateChange?.(newState);
  }

  /**
   * Emit error and update state
   */
  private emitError(code: CameraErrorCode, message: string, originalError?: Error): void {
    const error: CameraErrorInfo = { code, message, originalError };
    this.setState('error');
    this.config.onError?.(error);
  }

  /**
   * Fit overlay canvas to match video dimensions
   */
  fitCanvas(): void {
    if (!this.overlayCanvas || !this.videoElement) return;

    const width = this.videoElement.videoWidth || this.overlayCanvas.clientWidth;
    const height = this.videoElement.videoHeight || this.overlayCanvas.clientHeight;

    // Only update if dimensions changed to avoid unnecessary redraws
    if (this.overlayCanvas.width !== width || this.overlayCanvas.height !== height) {
      this.overlayCanvas.width = width;
      this.overlayCanvas.height = height;
    }
  }

  /**
   * Set up resize listeners for canvas synchronization
   */
  private setupResizeListeners(): void {
    this.boundResizeHandler = () => this.fitCanvas();

    // Use ResizeObserver for better performance if available
    if (typeof ResizeObserver !== 'undefined' && this.videoElement) {
      this.resizeObserver = new ResizeObserver(() => this.fitCanvas());
      this.resizeObserver.observe(this.videoElement);
    }

    // Fallback to window events
    window.addEventListener('resize', this.boundResizeHandler);
    window.addEventListener('orientationchange', this.boundResizeHandler);
  }

  /**
   * Clean up resize listeners
   */
  private cleanupResizeListeners(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.boundResizeHandler) {
      window.removeEventListener('resize', this.boundResizeHandler);
      window.removeEventListener('orientationchange', this.boundResizeHandler);
      this.boundResizeHandler = null;
    }
  }

  /**
   * Start the camera and attach to video element
   */
  async start(
    videoElement: HTMLVideoElement,
    overlayCanvas?: HTMLCanvasElement
  ): Promise<MediaStream> {
    // Stop any existing stream first
    this.stop();

    this.videoElement = videoElement;
    this.overlayCanvas = overlayCanvas || null;

    this.setState('requesting');

    try {
      // Check if camera API is supported (older browsers, restricted contexts)
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(
          'Camera API not supported in this browser. ' +
            'Please use a modern browser (Chrome, Firefox, Safari, Edge) with HTTPS.'
        );
      }

      // Build constraints
      const constraints = this.config.constraints
        ? {
            video: {
              facingMode: { ideal: this.config.constraints.facingMode || 'user' },
              ...(this.config.constraints.width && { width: this.config.constraints.width }),
              ...(this.config.constraints.height && { height: this.config.constraints.height }),
            },
            audio: false,
          }
        : getDefaultConstraints();

      // Request camera access
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Track stream for global cleanup
      trackStream(this.stream);

      // Attach stream to video element
      this.videoElement.srcObject = this.stream;

      // Configure video element for mobile compatibility
      this.videoElement.setAttribute('playsinline', 'true');
      this.videoElement.setAttribute('autoplay', 'true');
      this.videoElement.muted = true;

      // Apply mirror effect for selfie mode
      if (this.config.mirror) {
        this.videoElement.style.transform = 'scaleX(-1)';
      }

      // Wait for video metadata to load
      await new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('Video metadata load timeout'));
        }, 10000);

        this.videoElement!.onloadedmetadata = () => {
          clearTimeout(timeoutId);
          resolve();
        };

        this.videoElement!.onerror = () => {
          clearTimeout(timeoutId);
          reject(new Error('Video element error'));
        };
      });

      // Start playing
      await this.videoElement.play();

      // Fit canvas to video dimensions
      this.fitCanvas();

      // Set up resize listeners
      this.setupResizeListeners();

      this.setState('streaming');

      return this.stream;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      const code = mapErrorCode(err);

      let message: string;
      switch (code) {
        case 'PERMISSION_DENIED':
          message = t(
            'errors.cameraAccessDeniedDetailed',
            'Camera access was denied. Please allow camera permissions and try again.'
          );
          break;
        case 'NOT_FOUND':
          message = t(
            'errors.cameraNotFoundDetailed',
            'No camera found. Please connect a camera and try again.'
          );
          break;
        case 'NOT_READABLE':
          message = t(
            'errors.cameraInUseDetailed',
            'Camera is in use by another application. Please close other apps using the camera.'
          );
          break;
        case 'OVERCONSTRAINED':
          message = t(
            'errors.cameraOverconstrained',
            'Camera does not support the requested resolution. Trying with default settings...'
          );
          break;
        default:
          message = `Camera error: ${err.message}`;
      }

      this.emitError(code, message, err);
      throw error;
    }
  }

  /**
   * Pause the video stream (keeps stream active but pauses rendering)
   */
  pause(): void {
    if (this.videoElement && this.state === 'streaming') {
      this.videoElement.pause();
      this.setState('paused');
    }
  }

  /**
   * Resume the video stream
   */
  async resume(): Promise<void> {
    if (this.videoElement && this.state === 'paused') {
      await this.videoElement.play();
      this.setState('streaming');
    }
  }

  /**
   * Stop the camera and release resources
   */
  stop(): void {
    // Stop all tracks and untrack from global registry
    if (this.stream) {
      stopStream(this.stream);
      this.stream = null;
    }

    // Clean up video element
    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.srcObject = null;
      this.videoElement.style.transform = '';
    }

    // Clean up listeners
    this.cleanupResizeListeners();

    this.videoElement = null;
    this.overlayCanvas = null;

    this.setState('stopped');
  }

  /**
   * Capture a frame from the video as ImageData
   */
  captureFrame(): ImageData | null {
    if (!this.videoElement || this.state !== 'streaming') {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = this.videoElement.videoWidth;
    canvas.height = this.videoElement.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Handle mirror if enabled
    if (this.config.mirror) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(this.videoElement, 0, 0);

    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  }

  /**
   * Capture a frame as a Blob (JPEG or PNG)
   */
  async captureBlob(
    type: 'image/jpeg' | 'image/png' = 'image/jpeg',
    quality = 0.9
  ): Promise<Blob | null> {
    if (!this.videoElement || this.state !== 'streaming') {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = this.videoElement.videoWidth;
    canvas.height = this.videoElement.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Handle mirror if enabled
    if (this.config.mirror) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(this.videoElement, 0, 0);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), type, quality);
    });
  }

  /**
   * Capture a frame as a data URL
   */
  captureDataURL(type: 'image/jpeg' | 'image/png' = 'image/jpeg', quality = 0.9): string | null {
    if (!this.videoElement || this.state !== 'streaming') {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = this.videoElement.videoWidth;
    canvas.height = this.videoElement.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Handle mirror if enabled
    if (this.config.mirror) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(this.videoElement, 0, 0);

    return canvas.toDataURL(type, quality);
  }

  /**
   * Get overlay canvas 2D context for drawing
   */
  getOverlayContext(): CanvasRenderingContext2D | null {
    if (!this.overlayCanvas) return null;
    return this.overlayCanvas.getContext('2d');
  }

  /**
   * Clear the overlay canvas
   */
  clearOverlay(): void {
    const ctx = this.getOverlayContext();
    if (ctx && this.overlayCanvas) {
      ctx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
    }
  }

  /**
   * Check if camera permissions are granted
   */
  static async checkPermissions(): Promise<PermissionState> {
    try {
      const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
      return result.state;
    } catch {
      // Permissions API not supported, return 'prompt' as default
      return 'prompt';
    }
  }

  /**
   * List available video input devices
   */
  static async listDevices(): Promise<MediaDeviceInfo[]> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter((device) => device.kind === 'videoinput');
    } catch {
      return [];
    }
  }
}

// ============================================================
// Global Camera Cleanup Utilities
// ============================================================

/**
 * Registry of all active camera streams for global cleanup
 */
const activeStreams = new Set<MediaStream>();

/**
 * Track a media stream for global cleanup
 */
export function trackStream(stream: MediaStream): void {
  activeStreams.add(stream);
}

/**
 * Untrack a media stream (called when stream is properly stopped)
 */
export function untrackStream(stream: MediaStream): void {
  activeStreams.delete(stream);
}

/**
 * Stop all tracks in a MediaStream and clean up
 */
export function stopStream(stream: MediaStream | null | undefined): void {
  if (!stream) return;

  stream.getTracks().forEach((track) => {
    track.stop();
  });

  untrackStream(stream);
}

/**
 * Stop all active camera streams globally.
 * Use this in error handlers and cleanup routines.
 */
export function cleanupAllCameras(): void {
  activeStreams.forEach((stream) => {
    stopStream(stream);
  });
  activeStreams.clear();

  // Also stop the default camera manager if it exists
  if (defaultCameraManager) {
    defaultCameraManager.stop();
  }
}

/**
 * Check if there are any active camera streams
 */
export function hasActiveStreams(): boolean {
  return activeStreams.size > 0;
}

/**
 * Get count of active camera streams
 */
export function getActiveStreamCount(): number {
  return activeStreams.size;
}

/**
 * Setup automatic cleanup on page unload and visibility change.
 * Call this once during SDK initialization.
 */
let cleanupHandlersInstalled = false;

export function installCleanupHandlers(): void {
  if (cleanupHandlersInstalled || typeof window === 'undefined') return;

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    cleanupAllCameras();
  });

  // Cleanup when page becomes hidden (mobile tab switch, etc.)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // Optional: pause instead of stop if you want to resume
      // For now, we keep the stream active on visibility change
      // Uncomment below to stop on visibility change:
      // cleanupAllCameras();
    }
  });

  // Cleanup on page navigation (SPA-friendly)
  window.addEventListener('pagehide', () => {
    cleanupAllCameras();
  });

  cleanupHandlersInstalled = true;
}

/**
 * Remove cleanup handlers (for testing or explicit control)
 */
export function removeCleanupHandlers(): void {
  cleanupHandlersInstalled = false;
  // Note: We don't actually remove the listeners since we don't store references
  // In practice, this is mainly for resetting the flag in tests
}

// ============================================================
// Export singleton instance for simple usage
// ============================================================

let defaultCameraManager: CameraManager | null = null;

export function getDefaultCameraManager(): CameraManager {
  if (!defaultCameraManager) {
    defaultCameraManager = new CameraManager();
    // Install cleanup handlers when first camera manager is created
    installCleanupHandlers();
  }
  return defaultCameraManager;
}

// Legacy function exports for backwards compatibility
export async function startCamera(
  video: HTMLVideoElement,
  overlay?: HTMLCanvasElement
): Promise<MediaStream> {
  return getDefaultCameraManager().start(video, overlay);
}

export function stopCamera(): void {
  getDefaultCameraManager().stop();
}

export function fitCanvas(): void {
  getDefaultCameraManager().fitCanvas();
}
